import { useEffect, useRef, useState } from "react";
import { api, apiErr, API, handleSignedOut } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { ConfidenceBadge, InsufficientBadge, ConflictBanner, Pill } from "@/components/AnswerBits";
import { HistoryPanel } from "@/components/HistoryPanel";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, FileText, ThumbsUp, ThumbsDown, Flag, AlertCircle, ExternalLink, Loader2, ChevronDown, ChevronRight, Copy, RotateCcw, ShieldCheck, History, RefreshCcw } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

const ALL = "__all__";

// Conversational "thinking" lines shown on the streaming badge while an answer is
// being generated. One is picked per question so it stays stable for that turn.
const THINKING_PHRASES = [
  "Checking the fine print...",
  "Consulting the mortgage brain trust...",
  "Crunching some imaginary numbers...",
  "Flipping through the guidelines...",
  "Putting on my underwriter hat...",
  "Doing some mortgage math...",
  "Checking it twice. Underwriters appreciate that.",
  "Digging a little deeper...",
  "Making sense of the mortgage maze...",
  "Getting my docs in order...",
  "Running it through the mortgage machine...",
  "Looking under the underwriting hood...",
  "Connecting the mortgage dots...",
  "Double-checking my work...",
  "Taking a quick trip through the guidelines...",
  "Making sure everything adds up...",
  "Searching the land of loans...",
  "Reading the fine print so you don't have to...",
  "Calculating... without opening Excel.",
  "Asking myself, 'Can we document that?'",
  "Channeling my inner underwriter...",
  "Making sure the DTI behaves...",
  "Hunting for the right guideline...",
  "Turning mortgage-speak into human-speak...",
  "Checking the footnotes. Yes, even those.",
  "Working toward a clear-to-answer...",
  "Giving this the full underwriting treatment...",
  "Running the numbers one more time...",
  "Reviewing, calculating, caffeinating...",
  "Almost there. No additional conditions required.",
  "Shuffling some digital paperwork...",
  "Checking with my imaginary underwriter...",
  "Making the guidelines earn their keep...",
  "Doing responsible mortgage things...",
  "Searching for wisdom in the footnotes...",
  "Pretending this is a perfectly normal loan file...",
  "Getting all my ducks in underwriting order...",
  "Applying an unreasonable amount of mortgage knowledge...",
  "Looking for the shortest path to an answer...",
  "Wading through the wonderful world of lending...",
  "Checking the guidelines behind the guidelines...",
  "Putting my mortgage nerd powers to work...",
  "Making sure nobody needs a Letter of Explanation...",
  "Crossing the T's and verifying the I's...",
  "Working some underwriting magic...",
  "Searching for the ever-elusive simple answer...",
  "Giving this a quick mortgage stress test...",
  "Running this past my inner loan officer...",
  "Doing a little guideline archaeology...",
  "Checking every box. Figuratively, of course.",
  "Looking for anything hiding in Schedule C...",
  "Processing... without asking for another bank statement.",
  "Keeping calm and calculating on...",
  "Going where few dare to go: the guidelines.",
  "Making sense of lending, one rule at a time...",
  "Reviewing everything with appropriate suspicion...",
  "Working on it. Please don't open any new credit.",
  "Putting years of imaginary mortgage experience to work...",
  "Thinking like an underwriter. Wish me luck.",
  "Getting closer to Clear to Answer...",
];

const pickThinking = () => THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)];

// Build a context-aware greeting pool: always-on lines plus time/day/month-aware
// ones that only appear when appropriate. `name` is the logged-in user's first name.
function buildGreetings(name) {
  const who = name || "there";
  const d = new Date();
  const hour = d.getHours();
  const dow = d.getDay(); // 0 Sun ... 5 Fri
  const date = d.getDate();

  const everyday = [
    "Welcome back! What are we solving today — income, assets, guidelines, or one of those files?",
    "Hey there. Got a mortgage question? I've got guidelines and zero plans for lunch.",
    "Welcome back! Let's get this loan moving.",
    "What's on the docket today? Conventional, FHA, VA, USDA, or something weird?",
    "Another day, another loan. What can I help you figure out?",
    "Need a second set of eyes? I don't technically have eyes, but you get the idea.",
    "Let's talk mortgages. What's giving you trouble today?",
    "Got a borrower scenario? Throw it at me.",
    "Income calculation? Product question? Underwriting mystery? I'm ready.",
    "Welcome back. Let's turn some question marks into clear-to-close.",
    "Welcome back. Somewhere out there, a file is missing exactly one page of a bank statement. Let's go find it.",
    "You're in. Fannie, Freddie, FHA, VA, and USDA are all standing by with their arms crossed, waiting for your question.",
    "Ready when you are. Fair warning: my answer may begin with 'it depends on the AUS findings.'",
    "Ask me anything. I've read the seller guide cover to cover so you never have to.",
    "Today's forecast: 80% chance of conditions, clearing by Thursday.",
    "Coffee acquired? Great. Let's underwrite.",
    "Welcome back! May your ratios be low and your conditions be few.",
    "DTI looking a little spicy today? Let's take a look.",
    "Ask away. I promise not to add another condition.",
    "I've read the guidelines so you don't have to. Okay, you probably still have to.",
    "Welcome! Let's find the answer before someone says, 'But we did it on the last loan.'",
    "Another beautiful day in the land of income, assets, and inexplicable bank deposits.",
    "Welcome back. The guidelines are long. Your question doesn't have to be.",
    "Got a scenario that starts with, 'So, this one's a little unusual…'? Those are my favorite.",
    "Mortgage math before coffee? Bold choice. I'm here for you.",
    "I'm ready. Self-employed borrower with three businesses and a 247-page tax return?",
    "Good news. You've reached someone who actually enjoys reading underwriting guidelines.",
    "Ask me anything mortgage-related. Except when rates are coming down. Nobody knows.",
    "Welcome back. Let's get you one step closer to the magical words: Clear to Close.",
    "What can I help with? And no, 'the borrower said it should be fine' isn't documentation.",
    "Got conditions? Let's make them disappear. The legitimate way.",
    "Today's forecast: 80% chance of documentation with scattered conditions.",
    "Welcome! Remember, screenshots are not always documentation. I know. I'm sorry.",
    "Let's solve this before you have to send underwriting another 'quick question.'",
    "Your borrower has how many sources of income? Okay. Tell me everything.",
    "Welcome back. Somewhere, a borrower is about to make a large undocumented deposit.",
    "Ask your question. I'm already emotionally prepared for the words 'self-employed.'",
    "Let's get started. Please tell me nobody bought a car before closing.",
    "Let's get this loan moving.",
    "What are we underwriting today?",
    "Bring me your weird scenarios.",
    "Guidelines? I've got you.",
    "Let's chase that Clear to Close.",
    "Mortgage question? Fire away.",
    "Let's crunch some ratios.",
    "What's the borrower up to now?",
    "Income, assets, credit. Pick your adventure.",
    "Let's solve a mortgage mystery.",
    "Conditions happen. Let's fix them.",
    "Okay, what did the borrower do?",
    "Another loan, another adventure.",
    "Let's make underwriting happy.",
    "Hit me with the scenario.",
    "Welcome back. Let's hope today's borrowers kept their receipts.",
    "Ready when you are. Please tell me the borrower didn't quit their job yesterday.",
    "Another day, another mysterious Venmo deposit.",
    "Welcome! Where every question eventually leads to 'Can we document that?'",
    "Let's get started. Gift funds, overtime, and questionable life choices welcome.",
    "Got a tricky file? Excellent. Normal borrowers are boring.",
    "Welcome back. May your appraisals be high and your DTIs be low.",
    "Ask away. I've already mentally prepared for the 84-page bank statement.",
    "Good to see you. Has the borrower financed any furniture today?",
    "Welcome! Let's find out if 'my uncle gave me the money' is enough documentation.",
    "Ready to roll. Please keep all unexplained deposits inside the vehicle.",
    "What's today's mystery? Income, assets, credit, or all of the above?",
    "Welcome back. I specialize in turning 'It should be fine' into actual answers.",
    "Let's solve some loans. Preferably before the borrower buys a boat.",
    "Hello again. Somewhere, an underwriter is asking for one more document.",
    "Got a complicated borrower? You had me at 'multiple Schedule Cs.'",
    "Welcome! Today's goal: fewer conditions, more closings.",
    "Ask me anything. Except why the borrower deposited $17,432 in cash yesterday.",
    "Back for more? The guidelines missed you.",
    "Let's do this. And remember, 'They've always been paid in cash' isn't the comforting statement you think it is.",
    "Welcome back. Let's see what fresh chaos the credit report has brought us.",
    "I'm ready. W-2 borrower or 14-LLC adventure?",
    "What can I help with? Bonus points if nobody changed jobs during underwriting.",
    "Welcome! Let's turn 'I think this works' into 'Yes, this works.'",
    "Another loan file, another opportunity to ask, 'Where did that money come from?'",
    "Come on in. The ratios are calculated and the conditions are multiplying.",
    "Welcome back. Your safe space for questions you don't want to ask underwriting yet.",
    "Got a borrower scenario? Make it weird. I can handle it.",
    "Let's get to work. Clear to Close isn't going to magically appear. Usually.",
    "Welcome back! Today is a great day to remind borrowers not to buy anything.",
    "Welcome back. Somewhere a borrower is financing a boat three days before closing. We can't stop them, but we can be ready.",
    "The appraisal came in. Deep breaths. What do you need to look up?",
    "Letters of explanation: the great American literary form. What can I help with?",
    "Hi! I'm the coworker who never says \"let me get back to you on that.\"",
    "\"Per guidelines\" is a complete sentence. Unfortunately nobody accepts it. Let's get you the actual citation.",
    "Reminder that a gift is only a gift if there's a letter saying so. What else can I clear up?",
    "Somewhere, a borrower is explaining a large deposit as \"birthday money.\" What's your question?",
    "Welcome. I have never once lost a document in an email thread. Ask away.",
    "Underwriting: where \"I'm self-employed\" becomes a four-hour math project. What are we solving?",
    "You bring the file, I'll bring the citation. Deal?",
    "Good to see you. No condition is unclearable — some just take a phone call.",
    "The borrower changed jobs mid-process. It's fine. It's fine. Ask me what we need.",
    "Everything is documentable. Some things just take three documents. What's the question?",
    "Welcome back. I'm caffeinated purely in spirit.",
    "Every guideline has an exception, and every exception has a paragraph. Let's go find yours.",
    "Hey there. Today's mystery: whose name is actually on that bank account?",
    "I don't sigh when you ask the same question twice. Truly, ask away.",
    "Rental income, vacancy factors, and Schedule E — the trilogy. Where do you want to start?",
    "Somewhere out there, an HOA is not answering the phone. Meanwhile, I'm right here.",
    "Nothing derails a file like a new credit inquiry. Ask me anything before it happens.",
    "Welcome. If the answer's in a guideline, I'll find it. If it isn't, I'll tell you that too.",
    "The file looks clean. That's usually right before the tax transcripts show up. What can I check?",
    "Overtime, bonus, commission — income that exists but still has to prove it. What are we averaging?",
    "Welcome back. Somebody's uncle is about to offer to co-sign something. Let's get ahead of it.",
    "You've got questions. I've got paragraphs. Let's trade.",
    "Nobody has ever read an entire guideline in one sitting. That's my job, not yours.",
    "Student loan payment calculation. I said it, we both flinched. Let's do it anyway.",
    "Welcome in. Reminder: \"my parents are just holding the money for me\" is not a documented asset.",
    "Somewhere a survey is being ordered far too late. Not on our watch. What do you need?",
    "I have never asked anyone to resend something I already had. What's your question?",
    "Second home or investment property? That one word changes everything. Let's talk.",
    "Hey. Big files, small questions, deeply weird edge cases — all welcome here.",
    "Good news: the guideline exists. Better news: I know exactly where it is.",
    "Welcome back. The borrower says they'll send it today. We'll see. What can I look up meanwhile?",
    "Somewhere, a handwritten paystub is being submitted in complete earnest. Stay strong.",
    "Assets are easy. Sourcing them is the hobby. What are we tracing?",
    "Every \"quick question\" in this business takes twenty minutes. Mine take seconds. Go ahead.",
    "Welcome. Fun fact: no one in recorded history has enjoyed a lock extension conversation.",
    "Occupancy, stated intent, and what the file actually shows. Let's line those up.",
    "You're logged in. Somewhere a septic inspection is being scheduled with tremendous reluctance.",
    "Hi. I read the entire bulletin so you can skim the summary.",
    "The borrower has a business account, a personal account, and a strong opinion about which is which. Let's sort it out.",
    "Welcome back. Today's word of the day: continuance. Ask me why it matters.",
    "Nothing in this job is obvious. That's not a you problem, that's a guideline problem.",
    "Hey there. Somewhere a power of attorney is being drafted at the last possible minute. What's your question?",
    "Non-borrowing spouse, non-occupant co-borrower, non-negotiable deadline. Which one is today?",
    "Welcome. I promise not to say \"it depends\" more than twice per conversation.",
    "The AUS said approve/eligible and you still don't trust it. Correct instinct. Let's verify.",
    "Somewhere out there, a chimney is holding up a closing. Let's handle what we can control.",
    "Good to see you. Bring me your weirdest scenario — those are my favorite.",
  ];

  const product = [
    "FHA question? I know the 4000.1 the way other people know song lyrics.",
    "VA loans: no down payment, all of the paperwork.",
    "Condo project review: the final boss of underwriting. What are we up against?",
    "Two years of returns, one confusing K-1, and a dream. Let's break down that income.",
  ];

  const encouraging = [
    "No question is too basic here. I have never once judged anyone for asking about a 4506-C.",
    "You don't have to memorize the guidelines. That's literally my entire job.",
    "Ask the question you think you should already know the answer to. Those are my favorite ones.",
  ];

  const contextual = [];
  if (hour >= 5 && hour < 12) contextual.push(`Morning, ${who}. Coffee first, then DTI. What's the file?`);
  if (dow === 5) contextual.push("It's Friday, which means someone will ask for a rush approval at 4:45. Let's get ahead of it.");
  if (hour >= 21 || hour < 5) contextual.push("It's late and you're researching seasoning requirements. That's dedication.");
  if (date >= 25) contextual.push("End of the month — which means everything is closing on the 30th. Ask fast, I'll answer faster.");

  return [...everyday, ...product, ...encouraging, ...contextual];
}

function AnswerMarkdown({ text }) {
  return (
    <div className="text-sm leading-6 space-y-2 text-[#303030]" data-testid="answer-summary">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...p }) => <p className="leading-6" {...p} />,
          h1: ({ node, ...p }) => <h3 className="font-semibold text-[15px] mt-3 mb-1 text-[#202223]" {...p} />,
          h2: ({ node, ...p }) => <h3 className="font-semibold text-[15px] mt-3 mb-1 text-[#202223]" {...p} />,
          h3: ({ node, ...p }) => <h3 className="font-semibold text-[15px] mt-3 mb-1 text-[#202223]" {...p} />,
          ul: ({ node, ...p }) => <ul className="space-y-1 my-1" {...p} />,
          li: ({ node, children, ...p }) => (
            <li className="flex gap-2" {...p}><span className="text-[#005180] mt-[1px]">•</span><span>{children}</span></li>
          ),
          strong: ({ node, ...p }) => <strong className="font-semibold text-[#202223]" {...p} />,
          a: ({ node, ...p }) => <a className="text-[#005180] underline" {...p} />,
          table: ({ node, ...p }) => (
            <div className="my-3 overflow-x-auto rounded-lg border border-[#E1E3E5]">
              <table className="w-full text-sm border-collapse" {...p} />
            </div>
          ),
          thead: ({ node, ...p }) => <thead className="bg-[#F7F7F8]" {...p} />,
          th: ({ node, ...p }) => <th className="border-b border-[#E1E3E5] px-3 py-2 text-left font-semibold text-[#202223] align-top" {...p} />,
          td: ({ node, ...p }) => <td className="border-b border-[#EDEEF0] px-3 py-2 align-top" {...p} />,
        }}
      >
        {text || ""}
      </ReactMarkdown>
    </div>
  );
}

function EvidenceCard({ c, highlighted, refCb }) {
  const token = localStorage.getItem("token");
  const isMarkdown = c.page_start === null || c.page_start === undefined;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${c.storage_path || ""}?auth=${token}`;
  const [open, setOpen] = useState(false);
  useEffect(() => { if (highlighted) setOpen(true); }, [highlighted]);
  return (
    <div ref={refCb} data-testid={`evidence-card-${c.id}`}
      className={`border rounded-xl bg-white transition-[border-color,box-shadow] duration-150 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${highlighted ? "border-[#1E7FC2] ring-2 ring-[#1E7FC2]/25" : c.cited ? "border-[#95E3C4]" : "border-[#E1E3E5] hover:border-[#C9CCCF]"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`evidence-toggle-${c.id}`}
        className="w-full text-left p-3 flex gap-2 items-start hover:bg-[#F7F7F8] rounded-xl transition-colors">
        {open ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0 text-[#6D7175]" />
              : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-[#6D7175]" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {c.cited && <Pill tone="success">Cited</Pill>}
            {!c.is_active && <Pill tone="warning">Superseded</Pill>}
            {c.source_updated && <Pill tone="warning" data-testid="evidence-source-updated">Updated since</Pill>}
            {c.content_type === "table" && <Pill tone="neutral">Table</Pill>}
            {c.footnote_flag && <Pill tone="warning">Footnote</Pill>}
            {isMarkdown
              ? <span className="font-mono-cite text-[11px] text-[#005180] font-medium">Section</span>
              : <span className="font-mono-cite text-[11px] text-[#005180] font-medium">
                  Pg {c.page_start}{c.page_end !== c.page_start ? `–${c.page_end}` : ""}
                </span>}
          </div>
          <div className="text-[13px] font-medium mt-1.5 break-words text-[#202223]" title={c.section_path}>{(c.section_path || "").replace(/\*\*/g, "")}</div>
          <div className="text-xs text-[#6D7175] truncate mt-0.5" title={`${c.lender} · ${c.document_title}`}>
            {c.lender} · {c.document_title}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-2.5 border-t border-[#EDEEF0]">
          <p className="text-sm whitespace-pre-wrap leading-6 text-[#303030]">{c.raw_text}</p>
          {isMarkdown
            ? <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#6D7175]" data-testid="markdown-source-note">
                <FileText className="h-3 w-3" /> Source text shown above (Markdown)
              </div>
            : <a href={pdfUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#005180] hover:underline" data-testid="open-source-pdf">
                <ExternalLink className="h-3 w-3" /> Open source PDF
              </a>}
        </div>
      )}
    </div>
  );
}

export default function Research() {
  const [filters, setFilters] = useState({ lenders: [], doc_types: [], product_types: [] });
  const [lender, setLender] = useState(ALL);
  const [productType, setProductType] = useState(ALL);
  const [docType, setDocType] = useState(ALL);
  const [question, setQuestion] = useState("");
  const { user } = useAuth();
  const [welcome] = useState(() => {
    const first = (user?.name || "").trim().split(/\s+/)[0];
    const list = buildGreetings(first);
    return list[Math.floor(Math.random() * list.length)];
  });
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selected, setSelected] = useState(null); // message obj for evidence panel
  const [highlight, setHighlight] = useState(null);
  const [showEvidence, setShowEvidence] = useState(false); // mobile evidence overlay
  const [sessions, setSessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(() => window.matchMedia("(min-width: 1280px)").matches);
  const [historySheet, setHistorySheet] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const chatEndRef = useRef(null);
  const lastUserRef = useRef(null);
  const prevUserCount = useRef(0);
  const evidenceRefs = useRef({});

  useEffect(() => {
    api.get("/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const loadHistory = () =>
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {}).finally(() => setHistoryLoading(false));
  useEffect(() => { loadHistory(); }, []);
  // A brand-new conversation gets its id on the first answer: refresh the list so it appears.
  useEffect(() => {
    if (sessionId && !sessions.some((s) => s.id === sessionId)) loadHistory();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHistory = () => {
    if (window.matchMedia("(min-width: 1280px)").matches) setHistoryOpen((o) => !o);
    else setHistorySheet(true);
  };

  const openConversation = async (id) => {
    if (busy || openingId) return;
    if (id === sessionId) { setHistorySheet(false); return; }
    setOpeningId(id);
    try {
      const r = await api.get(`/chat/sessions/${id}/messages`);
      let lastQ = "";
      const restored = r.data
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => {
          if (m.role === "user") { lastQ = m.content; return { role: "user", content: m.content }; }
          return {
            role: "assistant", reopened: true, question: m.question || lastQ, message_id: m.id,
            answer: m.answer || { answer_summary: m.content || "" }, evidence: m.evidence || [],
            source_updated: m.source_updated, answered_at: m.created_at,
          };
        });
      prevUserCount.current = restored.filter((m) => m.role === "user").length;
      setMessages(restored);
      setSessionId(id);
      setSelected([...restored].reverse().find((m) => m.role === "assistant") || null);
      setHighlight(null);
      setHistorySheet(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ block: "end" }), 60);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setOpeningId(null);
    }
  };

  // On a NEW question, pin that question near the top so the answer streams into
  // view below it (instead of jumping to the bottom of the response). While an
  // answer streams into an existing turn, don't auto-scroll.
  useEffect(() => {
    const uc = messages.filter((m) => m.role === "user").length;
    if (uc > prevUserCount.current) {
      prevUserCount.current = uc;
      setTimeout(() => lastUserRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [messages]);

  const ask = async (overrideQ) => {
    const q = (typeof overrideQ === "string" ? overrideQ : question).trim();
    if (!q || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }]);
    setQuestion("");

    // Placeholder assistant message that fills in as the stream arrives.
    const acc = {
      role: "assistant", streaming: true, question: q, thinking: pickThinking(),
      answer: { answer_summary: "", confidence: null, quotes: [], citations: [], conflict: { detected: false }, follow_ups: [] },
      evidence: [], message_id: null,
    };
    setMessages((m) => [...m, acc]);
    setSelected(acc);

    const paint = () => {
      const snapshot = { ...acc, answer: { ...acc.answer }, evidence: acc.evidence };
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = snapshot;
        return copy;
      });
      setSelected(snapshot);
    };

    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/chat/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          question: q,
          session_id: sessionId,
          lender: lender === ALL ? null : lender,
          product_type: productType === ALL ? null : productType,
          doc_type: docType === ALL ? null : docType,
          include_inactive: false,
        }),
      });
      if (resp.status === 401) {
        const body = await resp.json().catch(() => ({}));
        handleSignedOut(body.detail);
        return;
      }
      if (!resp.ok || !resp.body) throw new Error(`Request failed (${resp.status})`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last partial
        for (const evt of events) {
          const line = evt.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (data.type === "start") {
            acc.session_id = data.session_id;
            setSessionId(data.session_id);
          } else if (data.type === "evidence") {
            acc.evidence = data.evidence || [];
            paint();
          } else if (data.type === "retry") {
            acc.evidence = data.evidence || [];
            acc.answer = { ...acc.answer, answer_summary: "" };
            acc.thinking = data.product ? "Searching that product's guide more closely…" : "Searching the guidelines more closely…";
            paint();
          } else if (data.type === "delta") {
            acc.answer = { ...acc.answer, answer_summary: acc.answer.answer_summary + (data.content || "") };
            paint();
          } else if (data.type === "done") {
            acc.answer = data.answer;
            acc.evidence = data.evidence || acc.evidence;
            acc.message_id = data.message_id;
            acc.streaming = false;
            if (data.session_id) setSessionId(data.session_id);
            paint();
          } else if (data.type === "error") {
            acc.error = true;
            acc.streaming = false;
            acc.answer = { ...acc.answer, answer_summary: data.detail || "Something went wrong." };
            paint();
          }
        }
      }
      acc.streaming = false;
      paint();
    } catch (e) {
      acc.error = true;
      acc.streaming = false;
      acc.answer = { ...acc.answer, answer_summary: apiErr(e) };
      paint();
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendFeedback = async (messageId, rating, issue_type) => {
    try {
      await api.post("/feedback", { message_id: messageId, rating, issue_type });
      toast.success("Feedback recorded. Thank you.");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const focusEvidence = (chunkId) => {
    setHighlight(chunkId);
    setShowEvidence(true); // open the overlay on mobile; no-op visually on desktop
    setTimeout(() => {
      const el = evidenceRefs.current[chunkId];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const evidence = selected?.evidence || [];
  const activeFilters = [lender, productType, docType].filter((v) => v !== ALL).length;

  const newConversation = () => {
    if (busy) return;
    setMessages([]);
    setSessionId(null);
    setSelected(null);
    setHighlight(null);
    prevUserCount.current = 0;
  };

  return (
    <Layout>
      <div className="polaris flex flex-col h-full min-h-0 bg-background">
        {/* Page header + filters */}
        <div className="bg-white border-b border-[#E1E3E5] shrink-0">
          <div className="px-3 sm:px-6 pt-3 pb-2 flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-semibold text-[#202223] truncate" data-testid="page-title">Guideline Research</h1>
              <Pill tone="highlight" className="hidden sm:inline-flex" data-testid="grounded-pill">
                <ShieldCheck className="h-3 w-3" /> Answers cite your guidelines
              </Pill>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={toggleHistory} data-testid="history-toggle-button"
                aria-pressed={historyOpen}
                className={`h-8 gap-1.5 border-[#C9CCCF] text-[#202223] hover:bg-[#F1F2F4] shadow-[0_1px_0_rgba(0,0,0,0.05)] ${historyOpen ? "xl:bg-[#F1F2F4]" : "bg-white"}`}>
                <History className="h-3.5 w-3.5" /> History
              </Button>
              <Button variant="outline" size="sm" onClick={newConversation} disabled={busy || messages.length === 0}
                data-testid="new-conversation-button"
                className="h-8 gap-1.5 bg-white border-[#C9CCCF] text-[#202223] hover:bg-[#F1F2F4] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                <RotateCcw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">New conversation</span><span className="sm:hidden">New</span>
              </Button>
            </div>
          </div>
          <div className="px-3 sm:px-6 pb-3 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2" data-testid="filters-bar">
            <FilterSelect label="Lender" value={lender} onChange={setLender} options={filters.lenders} testid="filter-lender" />
            <FilterSelect label="Product" value={productType} onChange={setProductType} options={filters.product_types} testid="filter-product" />
            <FilterSelect label="Doc Type" value={docType} onChange={setDocType} options={filters.doc_types} testid="filter-doctype" />
            {activeFilters > 0 && (
              <button type="button" data-testid="clear-filters-button"
                onClick={() => { setLender(ALL); setProductType(ALL); setDocType(ALL); }}
                className="col-span-3 sm:col-span-1 justify-self-start text-sm font-medium text-[#005180] hover:text-[#05304F] hover:bg-[#F0F7FF] rounded-md px-2 py-1 transition-colors">
                Clear filters ({activeFilters})
              </button>
            )}
          </div>
        </div>

        {/* Split workspace */}
        <div className="flex flex-1 min-h-0">
        {historyOpen && (
          <aside className="hidden xl:flex w-64 shrink-0 flex-col min-h-0 bg-[#F7F7F8] border-r border-[#E1E3E5]" data-testid="history-sidebar">
            <HistoryPanel sessions={sessions} loading={historyLoading} activeId={sessionId} onOpen={openConversation} openingId={openingId} />
          </aside>
        )}
        <div className="grid grid-cols-12 flex-1 min-h-0 min-w-0">
          {/* Chat */}
          <div className="col-span-12 lg:col-span-9 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-5" data-testid="chat-scroll">
              <div className="max-w-3xl mx-auto space-y-4">
              {messages.length === 0 && (
                <div className="mt-10 sm:mt-16 bg-white border border-[#E1E3E5] rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6 sm:p-8 text-center fade-in" data-testid="welcome-card">
                  <img src={BRAND.logos.mark} alt="Candi" className="h-14 w-14 mx-auto object-contain" />
                  <h3 className="font-semibold mt-4 text-lg text-[#202223]" data-testid="welcome-intro">Hi, I'm Candi.</h3>
                  <p className="text-base text-[#45494D] mt-1.5 max-w-md mx-auto leading-6" data-testid="welcome-message">
                    {welcome}
                  </p>
                </div>
              )}
              {(() => {
                let lastUserIndex = -1;
                messages.forEach((m, i) => { if (m.role === "user") lastUserIndex = i; });
                return messages.map((m, i) => (
                  <div key={i} ref={i === lastUserIndex ? lastUserRef : undefined}>
                    <MessageBubble m={m} onSelect={() => setSelected(m)} selected={selected === m}
                      onCite={focusEvidence} onFeedback={sendFeedback} onFollowUp={(t) => ask(t)} />
                  </div>
                ));
              })()}
              <div ref={chatEndRef} />
              </div>
            </div>
            <div className="px-3 sm:px-6 pb-3 pt-2 shrink-0">
              <div className="max-w-3xl mx-auto">
              {evidence.length > 0 && (
                <button type="button" onClick={() => setShowEvidence(true)} data-testid="mobile-evidence-toggle"
                  className="lg:hidden w-full mb-2 flex items-center justify-center gap-2 text-sm font-medium text-[#202223] bg-white border border-[#C9CCCF] rounded-lg py-2 hover:bg-[#F1F2F4] transition-colors">
                  <FileText className="h-3.5 w-3.5 text-[#005180]" /> View cited evidence ({evidence.length})
                </button>
              )}
              <div className="flex gap-2 items-end bg-white border border-[#C9CCCF] rounded-xl p-2 shadow-[0_2px_6px_rgba(0,0,0,0.06)] focus-within:border-[#1E7FC2] focus-within:ring-2 focus-within:ring-[#1E7FC2]/20 transition-[border-color,box-shadow]">
                <Textarea value={question} onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  placeholder="Ask a guideline question…" rows={2}
                  className="resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-2 text-base sm:text-[15px]" data-testid="question-input" />
                <Button onClick={ask} disabled={busy || !question.trim()} data-testid="ask-button"
                  className="h-10 w-10 p-0 rounded-lg bg-[#05304F] hover:bg-[#005180] text-white shrink-0" aria-label="Send question">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              </div>
            </div>
          </div>

          {/* Evidence — hidden on mobile until toggled (full-screen overlay); static column on desktop */}
          <div className={`${showEvidence ? "fixed inset-0 z-50 flex" : "hidden"} lg:static lg:z-auto lg:flex col-span-12 lg:col-span-3 flex-col min-h-0 bg-[#F7F7F8] lg:border-l border-[#E1E3E5]`}>
            <div className="border-b border-[#E1E3E5] px-4 py-3 bg-white flex items-center justify-between shrink-0">
              <span className="font-semibold text-sm text-[#202223]">Cited evidence</span>
              <div className="flex items-center gap-3">
                <Pill tone="neutral" data-testid="evidence-count">{evidence.length} passages</Pill>
                <button type="button" onClick={() => setShowEvidence(false)} data-testid="mobile-evidence-close"
                  className="lg:hidden text-sm text-[#005180] font-semibold">Done</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2.5" data-testid="evidence-panel">
              {!selected && (
                <div className="text-center mt-16 px-4 text-sm text-[#6D7175]">
                  <FileText className="h-6 w-6 mx-auto mb-2 text-[#8C9196]" />
                  Ask a question to see the exact source passages here.
                </div>
              )}
              {evidence.map((c) => (
                <EvidenceCard key={c.id} c={c} highlighted={highlight === c.id}
                  refCb={(el) => (evidenceRefs.current[c.id] = el)} />
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
      <Sheet open={historySheet} onOpenChange={setHistorySheet}>
        <SheetContent side="left" className="w-[85vw] max-w-xs p-0 bg-[#F7F7F8] font-polaris" data-testid="history-sheet">
          <SheetTitle className="sr-only">Recent conversations</SheetTitle>
          <HistoryPanel sessions={sessions} loading={historyLoading} activeId={sessionId} onOpen={openConversation} openingId={openingId} inSheet />
        </SheetContent>
      </Sheet>
    </Layout>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <span data-testid={testid} className="block w-full sm:w-auto">
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 w-full sm:w-[170px] text-xs sm:text-[13px] px-2 sm:px-3 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04)] ${value !== ALL ? "bg-[#F0F7FF] border-[#BAE6FD] text-[#0369A1] font-medium" : "bg-white border-[#C9CCCF]"}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label}s</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    </span>
  );
}

function MessageBubble({ m, onSelect, selected, onCite, onFeedback, onFollowUp }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end fade-in">
        <div className="bg-[#05304F] text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] text-[15px] leading-6 shadow-[0_1px_2px_rgba(0,0,0,0.12)]" data-testid="user-message">
          {m.content}
        </div>
      </div>
    );
  }
  const a = m.answer || {};
  const idMapQuotes = a.quotes || [];
  const followUps = (a.follow_ups || []).filter(Boolean);

  const sourceList = () => {
    const cited = (m.evidence || []).filter((e) => e.cited);
    const src = cited.length ? cited : (m.evidence || []);
    const seen = new Map();
    src.forEach((e) => {
      const k = `${e.lender}||${e.document_title}`;
      if (!seen.has(k)) seen.set(k, { lender: e.lender, title: e.document_title });
    });
    return [...seen.values()];
  };
  const buildExport = () => {
    const sources = sourceList().map((s) => `- ${s.lender} · ${s.title}`).join("\n") || "- (none cited)";
    return `Q: ${m.question || ""}\n\n${a.answer_summary || ""}\n\nSource(s):\n${sources}\n\n— Candi, The Mortgage AI Assistant`;
  };
  const copyAnswer = async () => {
    try { await navigator.clipboard.writeText(buildExport()); toast.success("Answer copied with sources"); }
    catch { toast.error("Couldn't copy to clipboard"); }
  };
  const sources = m.streaming ? [] : sourceList();
  const sectionLabel = "text-xs font-semibold text-[#6D7175] mb-1.5";
  const fbBtn = "h-8 gap-1.5 text-xs bg-white border-[#C9CCCF] text-[#303030] hover:bg-[#F1F2F4] rounded-lg";

  return (
    <div onClick={onSelect} data-testid="assistant-message"
      className={`fade-in bg-white rounded-xl border cursor-pointer shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow] duration-150 overflow-hidden ${selected ? "border-[#1E7FC2] ring-2 ring-[#1E7FC2]/15" : "border-[#E1E3E5] hover:border-[#C9CCCF]"}`}>
      <div className="p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <img src={BRAND.logos.mark} alt="" className="h-6 w-6 object-contain" />
          <span className="text-sm font-semibold text-[#202223] mr-1">Candi</span>
          {m.streaming
            ? <Pill tone="highlight" data-testid="answer-streaming">
                <Loader2 className="h-3 w-3 animate-spin" /> {m.thinking || "Working on it…"}
              </Pill>
            : (a.insufficient_evidence ? <InsufficientBadge /> : <ConfidenceBadge level={a.confidence} />)}
          {m.source_updated && (
            <Pill tone="warning" data-testid="source-updated-badge" title="A guideline cited in this answer has been replaced or deactivated since it was given.">
              <RefreshCcw className="h-3 w-3" /> Source updated since
            </Pill>
          )}
          {m.reopened && m.answered_at && (
            <span className="ml-auto text-[11px] text-[#8C9196]" data-testid="answered-at">
              {new Date(m.answered_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
            </span>
          )}
        </div>
        {a.conflict?.detected && <div className="mb-3"><ConflictBanner description={a.conflict.description} /></div>}
        <AnswerMarkdown text={a.answer_summary} />

        {a.supporting_points?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {a.supporting_points.map((p, i) => (
              <li key={i} className="text-sm flex gap-2"><span className="text-[#005180]">•</span><span>{p}</span></li>
            ))}
          </ul>
        )}
      </div>

      {sources.length > 0 && (
        <div className="px-4 py-3 border-t border-[#EDEEF0]" data-testid="answer-sources">
          <div className={sectionLabel}>{sources.length > 1 ? "Sources" : "Source"}</div>
          <div className="flex flex-wrap gap-1.5">
            {sources.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 max-w-full rounded-lg border border-[#E1E3E5] bg-[#F7F7F8] px-2 py-1 text-xs text-[#303030]" data-testid="answer-source">
                <FileText className="h-3 w-3 shrink-0 text-[#005180]" />
                <span className="truncate"><span className="font-semibold">{s.lender}</span> · {s.title}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {idMapQuotes.length > 0 && (
        <div className="px-4 py-3 border-t border-[#EDEEF0] space-y-2">
          <div className={sectionLabel}>Exact quotes</div>
          {idMapQuotes.map((q, i) => (
            <blockquote key={i} onClick={(e) => { e.stopPropagation(); q.chunk_id && onCite(q.chunk_id); }}
              data-testid="answer-quote"
              className="border-l-[3px] border-[#0284C7] bg-[#F0F7FF] rounded-r-lg pl-3 pr-2 py-2 text-sm italic text-[#202223] hover:bg-[#E0EFFE] cursor-pointer transition-colors">
              "{q.text}"
              <span className="block not-italic font-mono-cite text-[11px] text-[#0369A1] mt-1">→ {q.source_id}</span>
            </blockquote>
          ))}
        </div>
      )}

      {!m.streaming && followUps.length > 0 && (
        <div className="px-4 py-3 border-t border-[#EDEEF0]" data-testid="follow-ups" onClick={(e) => e.stopPropagation()}>
          <div className={sectionLabel}>Suggested follow-ups</div>
          <div className="flex flex-col gap-1.5">
            {followUps.map((q, i) => (
              <button key={i} type="button" onClick={() => onFollowUp && onFollowUp(q)}
                data-testid="follow-up-question"
                className="text-left text-sm text-[#0369A1] border border-[#BAE6FD] bg-[#F0F7FF] rounded-lg px-3 py-2 hover:bg-[#E0EFFE] hover:border-[#7DD3FC] transition-colors flex items-center gap-2">
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                <span>{q}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!m.error && m.message_id && (
        <div className="px-4 py-2.5 border-t border-[#EDEEF0] bg-[#FAFAFB] flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <span className="text-xs font-medium text-[#6D7175] mr-1">Was this helpful?</span>
          <Button variant="outline" size="sm" className={fbBtn} onClick={() => onFeedback(m.message_id, "helpful")} data-testid="feedback-helpful">
            <ThumbsUp className="h-3 w-3 text-[#007A5A]" /> Helpful
          </Button>
          <Button variant="outline" size="sm" className={fbBtn} onClick={() => onFeedback(m.message_id, "not_helpful")} data-testid="feedback-not-helpful">
            <ThumbsDown className="h-3 w-3" /> Not helpful
          </Button>
          <Button variant="outline" size="sm" className={fbBtn} onClick={() => onFeedback(m.message_id, "not_helpful", "wrong_citation")} data-testid="feedback-wrong-citation">
            <AlertCircle className="h-3 w-3 text-[#B25900]" /> Wrong citation
          </Button>
          <Button variant="outline" size="sm" className={`${fbBtn} text-[#7A1408] hover:bg-[#FDE8E8] hover:text-[#7A1408]`} onClick={() => onFeedback(m.message_id, "not_helpful", "escalate")} data-testid="feedback-escalate">
            <Flag className="h-3 w-3 text-[#D72C0D]" /> Escalate
          </Button>
          <Button variant="outline" size="sm" className={`${fbBtn} ml-auto`} onClick={copyAnswer} data-testid="copy-answer">
            <Copy className="h-3 w-3" /> Copy
          </Button>
        </div>
      )}
    </div>
  );
}
